import { useState } from 'react';
import { useProjects, useTags } from '@/hooks/useTasks.ts';
import { startOfDay, startOfTomorrow, addDays } from '@/lib/dates.ts';
import { OPEN_STATUSES } from '@/lib/views.ts';
import { Chip, Input, Button } from './ui.tsx';
import { STATUS_LABELS } from '@/lib/grouping.ts';
import { TASK_STATUSES, PRIORITIES, PRIORITY_LABELS } from '@shared/types';
import type { TaskFilter, Priority } from '@shared/types';

/**
 * Due-date presets rather than a raw date picker.
 *
 * "Overdue" and "this week" are the questions actually asked of a backlog; a
 * pair of exact timestamps is a worse way to express them and easy to get
 * off-by-one. Each preset returns the same half-open bounds the server uses.
 */
const DUE_PRESETS = {
  any: () => ({}),
  overdue: () => ({ due_before: new Date().toISOString() }),
  today: () => ({ due_before: startOfTomorrow().toISOString() }),
  week: () => ({ due_before: addDays(startOfDay(), 7).toISOString() }),
  none: () => ({ no_due_date: true }),
} as const;

type DuePreset = keyof typeof DUE_PRESETS;

const DUE_LABELS: Record<DuePreset, string> = {
  any: 'Any date',
  overdue: 'Overdue',
  today: 'Due today',
  week: 'Next 7 days',
  none: 'No date',
};

interface Props {
  filter: TaskFilter;
  onChange: (filter: TaskFilter) => void;
}

export function FilterBar({ filter, onChange }: Props) {
  const { data: projects } = useProjects();
  const { data: tags } = useTags();
  const [expanded, setExpanded] = useState(false);

  /** Applies a preset by clearing all date keys first, so they cannot stack. */
  function setDuePreset(preset: DuePreset) {
    const { due_before: _b, due_after: _a, no_due_date: _n, ...rest } = filter;
    onChange({ ...rest, ...DUE_PRESETS[preset]() });
  }

  function currentPreset(): DuePreset {
    if (filter.no_due_date) return 'none';
    if (!filter.due_before) return 'any';
    // Distinguishing the three "before X" presets by their bound would be
    // fragile; matching the nearest is enough for showing which chip is active.
    const bound = new Date(filter.due_before).getTime();
    if (bound <= Date.now() + 60_000) return 'overdue';
    if (bound <= startOfTomorrow().getTime()) return 'today';
    return 'week';
  }

  function toggle<T>(list: T[] | undefined, value: T): T[] | undefined {
    const current = list ?? [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    return next.length ? next : undefined;
  }

  const activeCount =
    (filter.status?.length ? 1 : 0) +
    (filter.priority?.length ? 1 : 0) +
    (filter.project_id?.length ? 1 : 0) +
    (filter.tag_ids?.length ? 1 : 0) +
    (filter.due_before || filter.due_after || filter.no_due_date ? 1 : 0) +
    (filter.q ? 1 : 0);

  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={filter.q ?? ''}
          onChange={(e) => onChange({ ...filter, q: e.target.value || undefined })}
          placeholder="Search title and notes"
          aria-label="Search"
          className="min-w-0 flex-1"
        />

        <Button onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
          Filters{activeCount > 0 ? ` · ${activeCount}` : ''}
        </Button>

        {activeCount > 0 && (
          <Button variant="ghost" onClick={() => onChange({ top_level_only: filter.top_level_only ?? true })}>
            Clear
          </Button>
        )}
      </div>

      {expanded && (
        <div className="mt-3 space-y-4 rounded-md border border-line bg-raised p-4">
          <Group label="Status">
            <Chip
              active={!filter.status?.length}
              onClick={() => onChange({ ...filter, status: undefined })}
            >
              Any
            </Chip>
            <Chip
              active={
                filter.status?.length === OPEN_STATUSES.length &&
                OPEN_STATUSES.every((s) => filter.status?.includes(s))
              }
              onClick={() => onChange({ ...filter, status: [...OPEN_STATUSES] })}
            >
              Open only
            </Chip>
            {TASK_STATUSES.map((status) => (
              <Chip
                key={status}
                active={filter.status?.includes(status) ?? false}
                onClick={() => onChange({ ...filter, status: toggle(filter.status, status) })}
              >
                {STATUS_LABELS[status]}
              </Chip>
            ))}
          </Group>

          <Group label="Priority">
            <Chip
              active={!filter.priority?.length}
              onClick={() => onChange({ ...filter, priority: undefined })}
            >
              Any
            </Chip>
            {PRIORITIES.map((p) => (
              <Chip
                key={p}
                active={filter.priority?.includes(p) ?? false}
                onClick={() =>
                  onChange({ ...filter, priority: toggle(filter.priority, p as Priority) })
                }
              >
                {PRIORITY_LABELS[p]}
              </Chip>
            ))}
          </Group>

          <Group label="Due">
            {(Object.keys(DUE_PRESETS) as DuePreset[]).map((preset) => (
              <Chip
                key={preset}
                active={currentPreset() === preset}
                onClick={() => setDuePreset(preset)}
              >
                {DUE_LABELS[preset]}
              </Chip>
            ))}
          </Group>

          <Group label="Project">
            <Chip
              active={!filter.project_id?.length}
              onClick={() => onChange({ ...filter, project_id: undefined })}
            >
              Any
            </Chip>
            <Chip
              active={filter.project_id?.includes(null) ?? false}
              onClick={() => onChange({ ...filter, project_id: toggle(filter.project_id, null) })}
            >
              No project
            </Chip>
            {(projects ?? []).map((project) => (
              <Chip
                key={project.id}
                active={filter.project_id?.includes(project.id) ?? false}
                onClick={() =>
                  onChange({ ...filter, project_id: toggle(filter.project_id, project.id) })
                }
              >
                {project.name}
              </Chip>
            ))}
          </Group>

          {(tags ?? []).length > 0 && (
            <Group label="Tags">
              <Chip
                active={!filter.tag_ids?.length}
                onClick={() => onChange({ ...filter, tag_ids: undefined })}
              >
                Any
              </Chip>
              {(tags ?? []).map((tag) => (
                <Chip
                  key={tag.id}
                  active={filter.tag_ids?.includes(tag.id) ?? false}
                  onClick={() => onChange({ ...filter, tag_ids: toggle(filter.tag_ids, tag.id) })}
                >
                  #{tag.name}
                </Chip>
              ))}
              {(filter.tag_ids?.length ?? 0) > 1 && (
                <Chip
                  active={filter.tags_match_all ?? false}
                  onClick={() => onChange({ ...filter, tags_match_all: !filter.tags_match_all })}
                >
                  Match all
                </Chip>
              )}
            </Group>
          )}
        </div>
      )}
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="t-label mb-1.5 block">{label}</span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

